import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { clientsApi, projectsApi, settingsApi } from '../../api/endpoints.js';
import type { ClientFileSection, ProjectSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
  TextAreaField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  ContextItem,
  Facts,
  RecordGrid,
  RecordHeader,
  RecordLink,
} from './record-layout.js';

/**
 * The client file: everything this laboratory knows about one client.
 *
 * THE SECTIONS TELL THE TRUTH ABOUT THEMSELVES. Projects is real, because
 * projects exist. Work orders, reports and invoices belong to modules that
 * have not been built, so they say "not available yet" and name the phase that
 * will fill them in. They do NOT show a total of zero: a zero would assert
 * that this client has no invoices, which nothing has established, and a
 * laboratory could reasonably act on it (CLAUDE.md section 8).
 *
 * SUSPENSION IS SHOWN AS WHAT IT IS. A banner saying no new work may be
 * opened, sitting above a file that still shows every project already on it.
 */
export function ClientFilePage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const [prompt, setPrompt] = useState<'suspend' | 'reinstate' | null>(null);
  const [reason, setReason] = useState('');

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
    retry: false,
  });

  const file = useQuery({
    queryKey: ['client-file', id],
    queryFn: () => clientsApi.file(id),
    enabled: Boolean(id),
  });

  const projects = useQuery({
    queryKey: ['projects', { clientId: id }],
    queryFn: () => projectsApi.list({ clientId: id, pageSize: 50 }),
    enabled: Boolean(id) && can(PERMISSIONS.PROJECTS_VIEW),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client-file', id] });
    await queryClient.invalidateQueries({ queryKey: ['clients'] });
  };

  const suspend = useMutation({
    mutationFn: () => clientsApi.suspend(id, reason.trim()),
    onSuccess: async () => {
      await refresh();
      setPrompt(null);
      setReason('');
      // Raised from what the server actually did, not from the button press.
      show({ tone: 'success', message: t('clients.suspend.done') });
    },
  });

  const reinstate = useMutation({
    mutationFn: () => clientsApi.reinstate(id, reason.trim() || undefined),
    onSuccess: async () => {
      await refresh();
      setPrompt(null);
      setReason('');
      show({ tone: 'success', message: t('clients.reinstate.done') });
    },
  });

  if (file.isLoading) return <LoadingState label={t('common.loading')} />;
  if (file.error) {
    return (
      <ErrorState error={file.error} onRetry={() => void file.refetch()} />
    );
  }
  if (!file.data) return null;

  const { client, sections } = file.data;
  const timezone = settings.data?.timezone;

  const projectColumns: readonly DataColumn<ProjectSummary>[] = [
    {
      key: 'name',
      header: t('projects.title'),
      render: (project) => (
        <span className="font-medium">
          <RecordLink to={`/projects/${project.id}`}>
            {project.name ?? t('common.notRecorded')}
          </RecordLink>
        </span>
      ),
    },
    {
      key: 'reference',
      header: t('projects.reference'),
      render: (project) =>
        project.reference ? (
          <span dir="ltr" className="measure">
            {project.reference}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (project) => (
        <StatusBadge
          label={t(`projects.status.${project.status}`)}
          tone={project.status === 'active' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (project) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/projects/${project.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  const notRecorded = t('common.notRecorded');
  const ltr = (value: string | null) =>
    value ? (
      <span dir="ltr" className="measure">
        {value}
      </span>
    ) : (
      notRecorded
    );

  return (
    <>
      <RecordHeader
        number={client.clientNumber}
        badges={
          <StatusBadge
            label={t(`clients.status.${client.status}`)}
            tone={client.status === 'active' ? 'success' : 'neutral'}
          />
        }
        title={client.name ?? client.clientNumber}
        context={
          <>
            <ContextItem label={t('clients.clientType')}>
              {t(`clients.type.${client.clientType}`)}
            </ContextItem>
            {client.city ? (
              <ContextItem label={t('clients.city')}>
                <bdi>{client.city}</bdi>
              </ContextItem>
            ) : null}
          </>
        }
        breadcrumbs={[
          { label: t('nav.clients'), to: '/clients' },
          { label: t('clients.file.title') },
        ]}
        actions={
          <ActionBar>
            {can(PERMISSIONS.CLIENTS_UPDATE) ? (
              <ActionButton
                priority="secondary"
                onPress={() => navigate(`/clients/${id}/edit`)}
              >
                {t('common.edit')}
              </ActionButton>
            ) : null}
            {can(PERMISSIONS.CLIENTS_VIEW) ? (
              <ActionButton
                priority="secondary"
                onPress={() => navigate(`/clients/${id}/contacts`)}
              >
                {t('contacts.title')}
              </ActionButton>
            ) : null}
            {/* Phase 11: the client's financial record, apart from the file. */}
            {can(PERMISSIONS.STATEMENTS_VIEW) ? (
              <ActionButton
                priority="secondary"
                onPress={() => navigate(`/clients/${id}/statement`)}
              >
                {t('clients.file.viewStatement')}
              </ActionButton>
            ) : null}
            {can(PERMISSIONS.CLIENTS_SUSPEND) ? (
              <ActionButton
                priority={client.acceptsNewDealings ? 'destructive' : 'primary'}
                onPress={() =>
                  setPrompt(client.acceptsNewDealings ? 'suspend' : 'reinstate')
                }
              >
                {client.acceptsNewDealings
                  ? t('clients.suspend.action')
                  : t('clients.reinstate.action')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      {!client.acceptsNewDealings ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('clients.suspend.banner')}
            {...(client.suspensionReason
              ? { description: client.suspensionReason }
              : {})}
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            <Panel title={t('clients.identity')}>
              <Facts
                items={[
                  { label: t('clients.clientNumber'), value: ltr(client.clientNumber) },
                  {
                    label: t('clients.name'),
                    value: <span dir="auto">{client.name}</span>,
                  },
                  {
                    label: t('clients.clientType'),
                    value: t(`clients.type.${client.clientType}`),
                  },
                  {
                    label: t('clients.vatRegistrationNumber'),
                    value: ltr(client.vatRegistrationNumber),
                  },
                  {
                    label: t('clients.commercialRegistrationNumber'),
                    value: ltr(client.commercialRegistrationNumber),
                  },
                  {
                    label: t('common.createdAt'),
                    value: formatDate(client.createdAt, {
                      locale,
                      ...(timezone ? { timezone } : {}),
                    }),
                  },
                ]}
              />
            </Panel>

            <Panel title={t('clients.contactDetails')}>
              <Facts
                items={[
                  { label: t('clients.contactEmail'), value: ltr(client.contactEmail) },
                  { label: t('clients.contactPhone'), value: ltr(client.contactPhone) },
                  {
                    label: t('clients.address'),
                    value: client.addressLine ? <bdi>{client.addressLine}</bdi> : notRecorded,
                  },
                  {
                    label: t('clients.city'),
                    value: client.city ? <bdi>{client.city}</bdi> : notRecorded,
                  },
                ]}
              />
            </Panel>
          </>
        }
      >
        {/*
          The four aggregate sections, in one panel. Only some of them can
          answer, and the others say so rather than showing a zero.
        */}
        <Panel title={t('ordersRecord.onFile')}>
          <ul className="grid gap-x-gutter gap-y-stack sm:grid-cols-2 lg:grid-cols-4">
            <FileSectionCard
              label={t('clients.file.projects')}
              section={sections.projects}
            />
            <FileSectionCard
              label={t('clients.file.workOrders')}
              section={sections.workOrders}
            />
            <FileSectionCard
              label={t('clients.file.reports')}
              section={sections.reports}
            />
            <FileSectionCard
              label={t('clients.file.invoices')}
              section={sections.invoices}
              {...(can(PERMISSIONS.FINANCE_VIEW)
                ? { onOpen: () => navigate(`/invoices?clientId=${id}`) }
                : {})}
            />
          </ul>
        </Panel>

        {can(PERMISSIONS.PROJECTS_VIEW) ? (
          <Panel
            title={t('clients.file.projects')}
            actions={
              can(PERMISSIONS.PROJECTS_CREATE) && client.acceptsNewDealings ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() => navigate(`/projects/new?clientId=${id}`)}
                >
                  {t('projects.create')}
                </ActionButton>
              ) : null
            }
          >
            <DataTable
              label={t('clients.file.projects')}
              columns={projectColumns}
              rows={projects.data?.data ?? []}
              rowKey={(project) => project.id}
              isLoading={projects.isLoading}
              error={projects.error}
              onRetry={() => void projects.refetch()}
              emptyContent={t('projects.noneForClient')}
            />
          </Panel>
        ) : null}
      </RecordGrid>

      <ConfirmDialog
        isOpen={prompt === 'suspend'}
        onClose={() => setPrompt(null)}
        title={t('clients.suspend.title')}
        message={t('clients.suspend.message')}
        confirmLabel={t('clients.suspend.action')}
        confirmPriority="destructive"
        isPending={suspend.isPending}
        onConfirm={() => suspend.mutate()}
      >
        <TextAreaField
          label={t('clients.suspend.reason')}
          description={t('clients.suspend.reasonRequired')}
          value={reason}
          onChange={setReason}
          isRequired
          rows={3}
        />
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={prompt === 'reinstate'}
        onClose={() => setPrompt(null)}
        title={t('clients.reinstate.title')}
        message={t('clients.reinstate.message')}
        confirmLabel={t('clients.reinstate.action')}
        isPending={reinstate.isPending}
        onConfirm={() => reinstate.mutate()}
      >
        <TextAreaField
          label={t('common.reasonOptional')}
          value={reason}
          onChange={setReason}
          rows={2}
        />
      </ConfirmDialog>
    </>
  );
}

/**
 * One aggregate section.
 *
 * The distinction this component exists to draw: `0` and "we cannot say" look
 * identical on a dashboard and mean entirely different things. A count is
 * rendered only when the module that owns it can actually produce one.
 */
function FileSectionCard({
  label,
  section,
  onOpen,
}: {
  readonly label: string;
  readonly section: ClientFileSection;
  /** Present only once a module both HAS this count and lets this user open it. */
  readonly onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const isAvailable = section.state === 'available';

  return (
    // One line of the "on file" panel, not a card of its own (D278).
    <li className="min-w-0">
      <p className="text-caption text-default-600">{label}</p>

      {isAvailable ? (
        <>
          <p className="measure mt-0.5 text-section font-semibold text-default-900" dir="ltr">
            {section.count}
          </p>
          {section.count === 0 ? (
            <p className="text-caption text-default-600">
              {t('clients.file.noneYet')}
            </p>
          ) : null}
          {onOpen ? (
            <ActionButton priority="tertiary" size="sm" onPress={onOpen}>
              {t('common.open')}
            </ActionButton>
          ) : null}
        </>
      ) : (
        <>
          {/* No number at all. Not a zero, not a dash pretending to be one. */}
          <p className="mt-0.5 text-body font-medium text-default-700">
            {t('clients.file.notImplemented')}
          </p>
          <p className="text-caption text-default-600">
            {t('clients.file.notImplementedHint')}
          </p>
          {section.plannedPhase ? (
            <p className="mt-1 text-caption text-default-600">
              {t('clients.file.plannedPhase', { phase: section.plannedPhase })}
            </p>
          ) : null}
        </>
      )}
    </li>
  );
}
